/**
 * CreateLibraryDialog - Dialog for creating a new library from index channel
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Chip,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
} from '@mui/icons-material';

interface CreateLibraryDialogProps {
  open: boolean;
  initialName: string;
  isLoading?: boolean;
  onClose: () => void;
  onSubmit: (data: {
    canonical_name: string;
    slug: string;
    notes: string;
    aliases: string[];
  }) => void;
}

const CreateLibraryDialog: React.FC<CreateLibraryDialogProps> = ({
  open,
  initialName,
  isLoading = false,
  onClose,
  onSubmit,
}) => {
  const [canonicalName, setCanonicalName] = useState('');
  const [slug, setSlug] = useState('');
  const [notes, setNotes] = useState('');
  const [aliases, setAliases] = useState<string[]>([]);
  const [newAlias, setNewAlias] = useState('');

  // Reset form when dialog opens with new initial name
  useEffect(() => {
    if (open) {
      setCanonicalName(initialName);
      setSlug('');
      setNotes('');
      setAliases([initialName]); // Start with the hashtag as first alias
      setNewAlias('');
    }
  }, [open, initialName]);

  const handleAddAlias = () => {
    const trimmed = newAlias.trim();
    if (trimmed && !aliases.includes(trimmed)) {
      setAliases([...aliases, trimmed]);
      setNewAlias('');
    }
  };

  const handleRemoveAlias = (aliasToRemove: string) => {
    setAliases(aliases.filter((a) => a !== aliasToRemove));
  };

  const handleSubmit = () => {
    const trimmedName = canonicalName.trim();
    if (!trimmedName) {
      alert('Canonical name is required');
      return;
    }

    if (trimmedName.length < 2) {
      alert('Canonical name must be at least 2 characters long');
      return;
    }

    onSubmit({
      canonical_name: trimmedName,
      slug: slug.trim(),
      notes: notes.trim(),
      aliases: aliases.filter((a) => a.trim()),
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newAlias.trim()) {
      e.preventDefault();
      handleAddAlias();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Create New Library</Typography>
          <IconButton edge="end" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          <TextField
            label="Canonical Name"
            fullWidth
            required
            value={canonicalName}
            onChange={(e) => setCanonicalName(e.target.value)}
            helperText="The official, standardized name for this library"
            autoFocus
          />

          <TextField
            label="Slug"
            fullWidth
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            helperText="Optional URL-friendly identifier (e.g., 'bukhari-sahih')"
          />

          <TextField
            label="Notes"
            fullWidth
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            helperText="Optional notes about the library"
          />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Aliases
            </Typography>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Alternative names for this library
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 1 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Add an alias..."
                value={newAlias}
                onChange={(e) => setNewAlias(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <Button
                variant="outlined"
                onClick={handleAddAlias}
                disabled={!newAlias.trim()}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Box>

            {aliases.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {aliases.map((alias, index) => (
                  <Chip
                    key={index}
                    label={alias}
                    onDelete={() => handleRemoveAlias(alias)}
                    size="small"
                  />
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canonicalName.trim() || isLoading}
        >
          {isLoading ? 'Creating...' : 'Create Library'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateLibraryDialog;
